import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import rafSchd from 'raf-schd';

import { useResizeObserver } from '@hotjar/webapp/hooks/useResizeObserver';
import { trigger } from '@hotjar/webapp/tracking/tools/hotjar';
import { CONTROLS_AND_ELEMENTS_IDS } from '@hotjar/recordings/src/constants/heatmaps-retaker';

import { useRealTimeStore } from '../store/realTimeStore';
import type { EventMarker } from '../types';
import { SectionList } from './Section/SectionList';
import { ProgressHandle } from './ProgressHandle';
import { SectionTooltip } from './Section/SectionTooltip';
import { EventMarkerList } from './EventMarker/EventMarkerList';
import { TIMELINE_PADDING } from './constants';
import { UnderlineList } from '../Underline/UnderlineList';
import { findFirstFilterMatch } from '../utils/findFirstFilterMatch';

const TimelineDiv = styled.div`
  height: 46px;
  min-height: 46px;
  width: 100%;

  position: relative;
  // We overlap a bit over the control component to capture hovering event close to the timeline
  bottom: -8px;

  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 ${TIMELINE_PADDING}px 4px ${TIMELINE_PADDING}px;
`;

const WrapperDiv = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BackgroundBardiv = styled.div`
  height: 8px;
  width: 100%;
`;

let methodCalled = false;
const triggerOnceTimelineUsed = () => {
  if (!methodCalled) {
    trigger('used_new_player_timeline');
    methodCalled = true;
  }
};

interface Props {
  duration: number;
  jumpTo: (targetTime: number, uiPosition: string) => void;
  eventMarkers: EventMarker[];
  isDesktopRecording: boolean;
  siteId: number;
  isPlaying: boolean;
  setTimelineBox: (node: HTMLElement | null) => void;
  sidePanelEventMarkers?: EventMarker[];
}

export const Timeline = ({
  duration,
  jumpTo,
  eventMarkers,
  isDesktopRecording,
  siteId,
  isPlaying,
  setTimelineBox,
  sidePanelEventMarkers,
}: Props): JSX.Element => {
  const backgroundBarRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(backgroundBarRef);
  const setDraggedTime = useRealTimeStore((state) => state.setDraggedTime);
  const setHoveredTime = useRealTimeStore((state) => state.setHoveredTime);
  const setAllTimeAfterDrag = useRealTimeStore((state) => state.setAllTimeAfterDrag);
  const setIsDragging = useRealTimeStore((state) => state.setIsDragging);
  const isDragging = useRealTimeStore((state) => state.isDragging);
  const setIsHovering = useRealTimeStore((state) => state.setIsHovering);
  const isHovering = useRealTimeStore((state) => state.isHovering);
  const [isHoveringSurrounding, setIsHoveringSurrounding] = useState(false);
  const [isHoveringOneMarker, setIsHoveringOneMarker] = useState(false);
  const [backgroundBarX, setBackgroundBarX] = useState<number>(0);

  useLayoutEffect(() => {
    if (backgroundBarRef.current) {
      const { left } = (backgroundBarRef.current as HTMLDivElement).getBoundingClientRect();
      setBackgroundBarX(left);
    }
    // Listening to width is a way to recalculate backgroundBarX when we switch to fullscreen
  }, [setBackgroundBarX, width]);

  const handleDocumentMouseUp = useCallback(
    (clientX: number, uiPosition: string) => {
      if (clientX >= 0 && backgroundBarRef.current) {
        const newXDragInPx = Math.min(Math.max(0, clientX - backgroundBarX), width);
        const newXDragInTime = (newXDragInPx / width) * duration;
        // We should call jumpTo before setAllTimeAfterDrag to be able to detect
        // if it is a backward of forward move.
        jumpTo(newXDragInTime, uiPosition);
        setAllTimeAfterDrag(newXDragInTime);
        setIsDragging(false);
        triggerOnceTimelineUsed();
      }
    },
    [backgroundBarX, duration, width, jumpTo, setAllTimeAfterDrag, setIsDragging],
  );

  const handleDocumentMouseMove = useMemo(
    () =>
      rafSchd((clientX: number) => {
        if (clientX >= 0 && backgroundBarX) {
          const newXDragInPx = Math.min(Math.max(0, clientX - backgroundBarX), width);
          const newXDragInTime = (newXDragInPx / width) * duration;
          setDraggedTime(newXDragInTime);
        }
      }),
    [backgroundBarX, width, duration, setDraggedTime],
  );

  const handleDocumentMouseDown = useCallback(
    (clientX: number, newUiPosition: string) => {
      if (clientX >= 0 && backgroundBarX) {
        const newXDragInPx = Math.min(Math.max(0, clientX - backgroundBarX), width);
        const newXDragInTime = (newXDragInPx / width) * duration;
        setDraggedTime(newXDragInTime);
      }
      setIsDragging(true);

      const handleDocumentMouseMoveWrapper = (e: MouseEvent) => handleDocumentMouseMove(e.clientX);
      const handleDocumentMouseUpWrapper = (e: MouseEvent) => {
        document.removeEventListener('mousemove', handleDocumentMouseMoveWrapper);
        document.removeEventListener('mouseup', handleDocumentMouseUpWrapper);
        handleDocumentMouseUp(e.clientX, newUiPosition);
      };

      // Listen on the whole document
      // to follow the mouse and set isDragging to false when mouse up.
      document.addEventListener('mouseup', handleDocumentMouseUpWrapper);
      document.addEventListener('mousemove', handleDocumentMouseMoveWrapper);
    },
    [
      backgroundBarX,
      width,
      duration,
      setDraggedTime,
      setIsDragging,
      handleDocumentMouseMove,
      handleDocumentMouseUp,
    ],
  );

  const handleMouseMoveWhenHover = useMemo(
    () =>
      rafSchd((clientX: number) => {
        // We just care about the hover position when it's not dragging
        if (!useRealTimeStore.getState().isDragging && backgroundBarX && clientX >= 0) {
          const newXHoverInPx = Math.min(Math.max(0, clientX - backgroundBarX), width);
          // If it's hovering on the marker or handle
          // We need to calculate which page is under it to expand it
          const newXHoverInTime = (newXHoverInPx / width) * duration;
          setHoveredTime(newXHoverInTime);
        }
      }),
    [backgroundBarX, width, duration, setHoveredTime],
  );

  const firstFilterMatch = findFirstFilterMatch(eventMarkers, sidePanelEventMarkers);
  return (
    <TimelineDiv
      draggable="false"
      onMouseEnter={() => {
        setIsHoveringSurrounding(true);
      }}
      onMouseLeave={() => {
        setIsHoveringSurrounding(false);
      }}
      onMouseMove={(e) => {
        // We pass clientX directly to avoid a warning: "This synthetic event is reused for performance reasons."
        handleMouseMoveWhenHover(e.clientX);
      }}
      ref={setTimelineBox}
    >
      <WrapperDiv
        id={CONTROLS_AND_ELEMENTS_IDS.TIMELINE}
        onMouseEnter={() => {
          setIsHovering(true);
        }}
        onMouseLeave={() => {
          setIsHovering(false);
        }}
      >
        <BackgroundBardiv ref={backgroundBarRef} draggable="false">
          <SectionList
            width={width}
            duration={duration}
            isHoveringTimeline={isHovering}
            isHoveringSurrounding={isHoveringSurrounding}
            onMouseDown={handleDocumentMouseDown}
          />
        </BackgroundBardiv>

        <EventMarkerList
          eventMarkers={eventMarkers}
          isDesktopRecording={isDesktopRecording}
          siteId={siteId}
          width={width}
          onMouseDown={handleDocumentMouseDown}
          setIsHoveringOneMarker={setIsHoveringOneMarker}
          firstFilterMatch={firstFilterMatch}
        />

        <ProgressHandle
          onMouseDown={handleDocumentMouseDown}
          visible={!isPlaying || isDragging || isHoveringSurrounding}
          width={width}
          duration={duration}
        />

        {(isDragging || (isHovering && !isHoveringOneMarker)) && (
          <SectionTooltip
            id={CONTROLS_AND_ELEMENTS_IDS.SELECTION_TOOLTIP}
            isDragging={isDragging}
            width={width}
            duration={duration}
          />
        )}
      </WrapperDiv>
      <UnderlineList
        sidePanelEventMarkers={sidePanelEventMarkers}
        recordingDuration={duration}
        firstFilterMatch={firstFilterMatch}
      />
    </TimelineDiv>
  );
};
